import React, { useEffect, useState, RefObject, useRef, useContext } from "react";

import { useRouteData } from "react-static";
import { Link } from "@reach/router";
import { CinematicLang, LocationLink } from "@utils/data";

import { useAppDispatch } from "@components/PersistedData/hooks";
import { setSavedLocation } from "@components/PersistedData/locationSlice";
import { toggleVideoSound, toggleSubtitle, useCurrentSettings } from "../PersistedData/settingsSlice";

import { UseUrlParams, getMediaUrl, useCollect, usePrevLink } from "../../utils/navigation";
import { NavigationContext } from "../TransitionRoot/NavigationContext";
import { useI18n } from "../../I18nextProvider";

import { CinematicPlayerProps } from "./index.interface";

function CinematicPlayer({ cinematic }: CinematicPlayerProps) {
  const i18n = useI18n();
  const place: LocationLink = { ...cinematic.next, type: "place" };
  const next: UseUrlParams = cinematic.endOfGame ? { type: "home" } : place;
  const videoContainer: RefObject<HTMLDivElement> = useRef(null);
  const videoRef: RefObject<HTMLVideoElement> = useRef(null);
  const { lang } = useRouteData();
  const prevLink = usePrevLink(next);
  const collect = useCollect(place);
  const collectSelf = useCollect({
    type: "cinematic",
    id: cinematic.id
  });
  const { goto } = useContext(NavigationContext);
  const settings = useCurrentSettings();
  const dispatch = useAppDispatch();

  const cineLang: CinematicLang = cinematic[lang];
  const videoUrl = getMediaUrl(cineLang.videoSrc);
  const captionUrl = getMediaUrl(cineLang.captionSrc);
  const posterURL = cineLang.posterImage ? getMediaUrl(cineLang.posterImage, "posterImage") : "";

  const [readyToPlay, setReadyToPlay] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);

  /**
   * Save subtitle state
   */
  useEffect(() => {
    const video = videoRef?.current;
    const saveSubtitleState = () => {
      const tracks = video.textTracks;
      for (let i = 0; i < tracks.length; i += 1) {
        const t = tracks[i];
        if (t.kind === "subtitles") {
          const enabled = t.mode === "showing";
          if (enabled !== settings.subtitleEnabled) {
            dispatch(toggleSubtitle());
          }
        }
      }
    };

    if (!video) {
      return;
    }
    const id = setInterval(saveSubtitleState, 1000);
    return () => {
      clearInterval(id);
    };
  }, [settings.subtitleEnabled, videoRef?.current]);

  useEffect(() => {
    const video = videoRef?.current;
    if (video) {
      video.textTracks[0].mode = settings.subtitleEnabled ? "showing" : "hidden";
    }
  }, [videoRef?.current, settings.subtitleEnabled]);

  const ended = () => {
    collectSelf();
    collect();
    goto(prevLink.path, prevLink.state);
  };

  const handlePasserClick = (e) => {
    e.preventDefault();
    ended();
  };

  const handleVolumeChange = (e) => {
    const video = videoRef?.current;
    if (video) {
      if (video.muted === settings.videoSoundEnabled) {
        dispatch(toggleVideoSound());
      }
    }
  };
  const handleCanPlay = (e) => {
    setReadyToPlay(true);
  };
  const handleClickBugPlayBtn = (e) => {
    const video = videoRef?.current;
    if (video) {
      video.play();
    }
  };
  const handleOnPlay = (e) => {
    setHasStartedPlaying(true);
  };

  return (
    <div className="videoContainer showControls">
      <div className="videoWrapper" ref={videoContainer}>
        {settings.mode !== "noscript" && readyToPlay && !hasStartedPlaying && (
          <div className="btnStartVideo">
            <button type="button" onClick={handleClickBugPlayBtn}>
              <img src={require("../../assets/images/play-video-overlay.svg")} alt={i18n.t("global.jouer")} />
            </button>
          </div>
        )}
        <video
          src={videoUrl}
          crossOrigin="anonymous"
          controls
          muted={!settings.videoSoundEnabled}
          id="maincontent"
          tabIndex={-1}
          onCanPlay={handleCanPlay}
          onEnded={ended}
          onPlay={handleOnPlay}
          onVolumeChange={handleVolumeChange}
          ref={videoRef}
          poster={posterURL}
        >
          <track label="Sous titres" kind="captions" srcLang={lang} src={captionUrl} default />
          {cineLang.title}
        </video>
        <div className="boutons_video">
          {cineLang.transcription && (
            <details className="b_transcription" open={settings.mode === "noscript"}>
              <summary>
                {i18n.t("video.btn_transcription")}{" "}
                <img src={require("../../assets/images/transcription-icon.svg")} alt="transcription" />
              </summary>
              <div className="contenu" dangerouslySetInnerHTML={{ __html: cineLang.transcription }} />
            </details>
          )}
          <Link className="passer" to={prevLink.path} onClick={handlePasserClick}>
            {i18n.t("video.btn_passer")}
            <img src={require("../../assets/images/passer-icon.svg")} alt={i18n.t("video.btn_passer")} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default CinematicPlayer;
